import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flex, Form, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import HeaderForm from "./widgetsForm/HeaderForm";
import { setSelectedWidget } from "../../redux/slices/widgetsSlice";
import isEqual from 'lodash.isequal';
import TextForm from "./widgetsForm/TextForm";
import TextareaForm from "./widgetsForm/TextareaForm";
import DateForm from "./widgetsForm/DateForm";
import NumberForm from "./widgetsForm/NumberForm";
import InfoForm from "./widgetsForm/InfoForm";
import AttachmentForm from "./widgetsForm/AttachmentForm";
import AddressForm from "./widgetsForm/AddressForm";
import TimeForm from "./widgetsForm/TimeForm";
import ContactForm from "./widgetsForm/ContactForm";
import DropdownForm from "./widgetsForm/DropdownForm";
import ImageForm from "./widgetsForm/ImageForm";
import SectionForm from "./widgetsForm/SectionForm";
import SwitchForm from "./widgetsForm/SwitchForm";
import CheckboxForm from "./widgetsForm/CheckboxForm";
import MultipleChoiceForm from "./widgetsForm/MultipleChoiceForm";
import RadioForm from "./widgetsForm/RadioForm";
import LinkForm from "./widgetsForm/LinkForm";
import DividerForm from "./widgetsForm/DividerForm";
import TableForm from "./widgetsForm/TableForm";
import { setFormItemDefaultAttribute, getFormDefaultValues } from "../../utils/formsHelper";
import { getCurrentSpaceData } from "../../utils/currentUserDetail";
import UserPickerForm from "./widgetsForm/UserPickerForm";
import RatingForm from "./widgetsForm/RatingForm";
import RatingScaleForm from "./widgetsForm/RatingScaleForm";
import {
    FORM_LANGUAGE_OPTIONS,
    DEFAULT_LANGUAGE,
    extractTranslatableValues,
    buildTranslationDraft,
} from "../../utils/formTranslations";

const isMasterSourced = (element) => Boolean(element?.parentTable || element?.isMasterData)

// Master-backed dropdowns (and table columns) source option labels from master
// data. Drop those option snapshots from the translation draft so a later edit
// cannot overwrite the copies made when the column was added.
const extractPersistedTranslations = (values, element) => {
    const extracted = extractTranslatableValues(values)
    if (isMasterSourced(element) || isMasterSourced(values)) {
        delete extracted.options
    }
    if (Array.isArray(extracted.columns)) {
        extracted.columns = extracted.columns.map((col, index) => {
            const sourceCol = values?.columns?.[index] || {}
            const canonicalCol = element?.columns?.[index] || {}
            const pickId = (...vals) => vals.find((value) => value !== undefined && value !== null && value !== "")
            const id = pickId(col?.id, sourceCol.id, sourceCol._id, canonicalCol.id, canonicalCol._id)
            let next = { ...col }
            if (id != null) next.id = id
            else delete next.id
            if (isMasterSourced(sourceCol) || isMasterSourced(canonicalCol)) {
                const { options: _options, ...rest } = next
                next = rest
            }
            return next
        })
    }
    return extracted
}

const mergeTranslationBucket = (previous = {}, extracted = {}) => {
    const next = { ...previous, ...extracted }
    if (extracted.options === undefined && Array.isArray(previous.options)) {
        next.options = previous.options
    }
    if (Array.isArray(previous.columns) || Array.isArray(extracted.columns)) {
        const previousCols = Array.isArray(previous.columns) ? previous.columns : []
        const extractedCols = Array.isArray(extracted.columns) ? extracted.columns : []
        if (extractedCols.length === 0) {
            next.columns = previousCols
        } else {
            const columnId = (col) => (col?.id !== undefined && col?.id !== null && col.id !== "" ? col.id : undefined)
            const prevById = new Map()
            previousCols.forEach((col) => {
                const prevId = columnId(col)
                if (prevId != null) prevById.set(String(prevId), col)
            })
            next.columns = extractedCols.map((col, index) => {
                const colId = columnId(col) ?? columnId(previousCols[index])
                const existing = (colId != null ? prevById.get(String(colId)) : undefined) || previousCols[index] || {}
                const merged = { ...existing, ...col }
                if (colId != null) merged.id = colId
                if (col.options === undefined && Array.isArray(existing.options)) {
                    merged.options = existing.options
                }
                return merged
            })
        }
    }
    return next
}

const SiderRight = (props) => {
    const { activeElement, updateElement, formType, formElements, setValidateSidebar, defaultValues = {} } = props
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [editForm] = Form.useForm();
    const newFormValues = Form.useWatch([], editForm);
    const [initialSet, setInitialSet] = useState(false)
    // Which language the panel is currently editing. The default language edits
    // the canonical field config; other languages edit `field.translations[lng]`.
    const [editLang, setEditLang] = useState(DEFAULT_LANGUAGE)

    // "Enable Previous Data Fetch" is only meaningful when the space allows
    // copying requests using a key-value, so hide the toggle otherwise.
    const { spaceId } = useParams()
    const { userInfo, userGroupSpaces } = useSelector(state => state.user)
    const spaceData = getCurrentSpaceData(spaceId, userInfo?.spaceInfo, userGroupSpaces) || {}
    const showPreviousDataFetch = spaceData?.enableRequestCopyWithKeyValue === true || spaceData?.enableRequestCopyWithKeyValue === "true"

    // Does the selected widget carry a default value? Filtered the same way the
    // form's defaults are before they are saved, so blank rows don't count.
    const hasDefaultValues = useMemo(() => {
        if (!activeElement?.id) return false
        return Boolean(getFormDefaultValues(defaultValues || {})[activeElement.id])
    }, [defaultValues, activeElement?.id])

    // Which element the panel is showing. The form header carries no `id`, so it
    // is told apart by its type; keying on `id` alone never refilled the panel
    // when the header was selected, leaving its fields blank.
    const selectionKey = activeElement ? (activeElement.id || activeElement.formType) : null

    // Reset to the default language whenever a different widget is selected.
    useEffect(() => {
        setEditLang(DEFAULT_LANGUAGE)
    }, [selectionKey])

    useEffect(() => {
        if (!setValidateSidebar) return;

        const validateSidebarForm = async () => {
            if (!activeElement) return true;
            try {
                await editForm.validateFields();
                return true;
            } catch (err) {
                return false;
            }
        };

        setValidateSidebar(() => validateSidebarForm);
        return () => {
            if (setValidateSidebar) setValidateSidebar(null);
        };
    }, [activeElement, editForm, setValidateSidebar]);

    useEffect(() => {
        if (activeElement) {
            editForm.resetFields();
            let elementWithDefaultAttribute = setFormItemDefaultAttribute({ ...activeElement })
            if (editLang !== DEFAULT_LANGUAGE) {
                // Show this language's translations for every text setting (label,
                // placeholder, help text, option/column labels, ...), blank when
                // untranslated so the author can fill them in. All other settings
                // keep their shared values.
                elementWithDefaultAttribute = buildTranslationDraft(elementWithDefaultAttribute, editLang)
            }
            editForm.setFieldsValue({ ...elementWithDefaultAttribute })
        } else {
            setInitialSet(false)
        }
    }, [selectionKey, editLang])
    const handleFormClose = () => {
        dispatch(setSelectedWidget({}));
    };

    const persistWidget = (values = {}) => {
        const translations = editForm.getFieldValue("translations") || activeElement?.translations || values.translations
        updateElement({
            ...activeElement,
            ...values,
            ...(translations ? { translations } : {}),
        })
    }

    const handleFormChange = async (changedValues, allValues) => {
        // When editing a non-default language, only translatable text is persisted
        // (into `translations[lng]`). Shared settings are intentionally not editable
        // here so they stay defined once in the default language.
        if (editLang !== DEFAULT_LANGUAGE) {
            // Ant Design reports Form.List column edits as an array or as an
            // index-map object (`{ 0: { label } }`). Treat either as a text change
            // so column translations are not skipped.
            const columnsChanged = changedValues.columns !== undefined
            if (!columnsChanged && Object.keys(extractTranslatableValues(changedValues)).length === 0) {
                return
            }
            const extracted = extractPersistedTranslations(editForm.getFieldsValue(true), activeElement)
            const storedTranslations = editForm.getFieldValue("translations") || activeElement.translations || {}
            const nextTranslations = {
                ...storedTranslations,
                [editLang]: mergeTranslationBucket(
                    storedTranslations[editLang] || {},
                    extracted
                ),
            }
            editForm.setFieldValue("translations", nextTranslations)
            updateElement({ ...activeElement, translations: nextTranslations })
            return
        }
        // Special case: `DropdownForm` toggles `isFrequency` and programmatically sets `options`.
        // `form.setFieldsValue` doesn't always trigger `onValuesChange`, so schedule a next-tick
        // update using the latest form state.
        if (changedValues.isFrequency !== undefined && Object.keys(changedValues).length === 1) {
            setTimeout(() => {
                persistWidget(editForm.getFieldsValue(true));
            }, 0);
            return;
        }

        // Table column editor frequently updates nested fields using setFieldValue.
        // Use latest form snapshot to avoid one-step stale updates in the canvas.
        if (changedValues.columns !== undefined) {
            setTimeout(() => {
                persistWidget(editForm.getFieldsValue(true));
            }, 0);
            return;
        }

        persistWidget(allValues);
    }

    return (
        <div className={`sider-right-container ${!activeElement ? 'hidden' : ''}`}>
            <Flex align="center" justify="space-between" style={{ display: "flex", gap: 10, width: "100%" }}>
                <div className="text-color-primary text-weight-600 text-h4">{t("Edit Details")}</div>
                <div className="icon-medium">
                    <CloseOutlined onClick={handleFormClose} />
                </div>
            </Flex>
            {/* Language selector temporarily disabled*/} 
            {
                activeElement &&
                <div style={{ width: "100%" }}>
                    <Select
                        style={{ width: "100%" }}
                        value={editLang}
                        onChange={setEditLang}
                        options={FORM_LANGUAGE_OPTIONS}
                    />
                    {
                        editLang !== DEFAULT_LANGUAGE &&
                        <div className="text-small text-color-secondary" style={{ marginTop: 4 }}>
                            {t("Editing translation — only text labels are saved for this language. Other settings are shared across languages.")}
                        </div>
                    }
                </div>
            }
            
            {
                activeElement ?
                    <Flex className="widgets-container">
                        <Form form={editForm} layout="vertical" onValuesChange={handleFormChange} >
                            <FormWrapper activeElement={activeElement} form={editForm} formType={formType} formValues={newFormValues} formElements={formElements} editLang={editLang} updateElement={updateElement} showPreviousDataFetch={showPreviousDataFetch} hasDefaultValues={hasDefaultValues} />
                        </Form>
                    </Flex>
                    : <div className="text-regular text-color-primary">{t("Select a widget to edit details.")}</div>
            }

        </div>
    )
}

export default SiderRight

const FormWrapper = ({ activeElement, formType, formValues, form, formElements, editLang, updateElement, showPreviousDataFetch = false, hasDefaultValues = false }) => {
    // Deliberately the same dependency list the effect that used to drive
    // `setCurrentForm` had: `activeElement`, `formElements` and `updateElement` are
    // excluded so a canvas keystroke doesn't rebuild the whole panel. Keeping the
    // memo (rather than building the element inline) preserves React's
    // props-identity bailout, which is what stopped every Form.Item being recreated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => {
        switch (formType) {
            case "header":
                return <HeaderForm form={form} formValues={formValues} />

            case "section":
                return <SectionForm form={form} formValues={formValues} />

            case "divider":
                return <DividerForm form={form} formValues={formValues} />

            case "addressField":
                return <AddressForm form={form} formValues={formValues} />

            case "attachment":
                return <AttachmentForm form={form} formValues={formValues} />

            case "contactField":
                return <ContactForm form={form} formValues={formValues} showPreviousDataFetch={showPreviousDataFetch} />

            case "inputForm":
                return <TextForm form={form} formValues={formValues} showPreviousDataFetch={showPreviousDataFetch} />

            case "imageForm":
                return <ImageForm form={form} formValues={formValues} />

            case "textAreaForm":
                return <TextareaForm form={form} formValues={formValues} />

            case 'numberForm':
                return <NumberForm form={form} formValues={formValues} formWidgets={formElements} showPreviousDataFetch={showPreviousDataFetch} />

            case 'dateForm':
                return <DateForm form={form} formValues={formValues} formWidgets={formElements} />

            case 'infoForm':
                return <InfoForm form={form} formValues={formValues} editLang={editLang} />

            case 'timeForm':
                return <TimeForm form={form} formValues={formValues} />

            case 'switchForm':
                return <SwitchForm form={form} formValues={formValues} />

            case 'checkboxField':
                return <CheckboxForm form={form} formValues={formValues} />

            case 'dropdownField':
                return <DropdownForm {...activeElement} form={form} formValues={formValues} showPreviousDataFetch={showPreviousDataFetch} />

            case 'multipleChoiceField':
                return <MultipleChoiceForm form={form} formValues={formValues} />

            case 'radioField':
                return <RadioForm form={form} formValues={formValues} />

            case 'linkField':
                return <LinkForm form={form} formValues={formValues} />

            case 'userForm':
                return <UserPickerForm form={form} formValues={formValues} />

            case 'tableForm':
                return <TableForm form={form} formValues={formValues} hasDefaultValues={hasDefaultValues} />

            case 'ratingField':
                return <RatingForm form={form} formValues={formValues} />

            case 'ratingScaleField':
                return <RatingScaleForm form={form} formValues={formValues} onFieldChange={updateElement} />

            default:
                return null
        }
    }, [formType, formValues, editLang, showPreviousDataFetch, hasDefaultValues, activeElement?.id])
}
